<div class="view-container">
  <mat-card class="batch-view-card">
    @if (batch) {
      <div class="batch-view-card__header">
        <div class="batch-view-card__title-row">
          <div class="batch-view-card__icon">
            <mat-icon>inventory_2</mat-icon>
          </div>
          <div>
            <h1 class="batch-view-card__title">{{ batch.batch_number }}</h1>
            <p class="batch-view-card__subtitle">Batch details and fulfilled orders</p>
          </div>
        </div>

        @if (perm.canMutate()) {
          <button
            type="button"
            (click)="editMode = !editMode"
            mat-flat-button
            color="primary"
            class="batch-view-card__edit-btn"
          >
            {{ editMode ? 'Details' : 'Edit' }}
          </button>
        }
      </div>
    }

    <div class="batch-view-card__actions">
      <button type="button" mat-stroked-button class="batch-view-card__action-btn" (click)="backToList()">
        <mat-icon>arrow_back</mat-icon>
        Back to list
      </button>
      <button type="button" mat-stroked-button class="batch-view-card__action-btn" (click)="printBarcode()">
        <mat-icon>print</mat-icon>
        Print barcode
      </button>
    </div>

    @if (!editMode && batch) {
      <div class="batch-view-card__details-wrap">
        <table class="batch-view-card__details-table">
          <tr>
            <th>Batch number</th>
            <td>{{ batch.batch_number }}</td>
          </tr>
          <tr>
            <th>Medicine</th>
            <td>{{ batchMedicine ? batchMedicine.name : '' }}</td>
          </tr>
          <tr>
            <th>Dosage type</th>
            <td>{{ batchMedicineDosageType ? batchMedicineDosageType.additional_information : '' }}</td>
          </tr>
          <tr>
            <th>Package type</th>
            <td>
              {{
                batchMedicineDosagePackageType
                  ? batchMedicineDosagePackageType.package_type.type +
                    ' (' +
                    batchMedicineDosagePackageType.contained_quantity +
                    ')'
                  : ''
              }}
            </td>
          </tr>
          <tr>
            <th>Status</th>
            <td>{{ batch.status ? batch.status.status : '—' }}</td>
          </tr>
          <tr>
            <th>Quantity produced</th>
            <td>{{ batch.quantity_produced }}</td>
          </tr>
          <tr>
            <th>Quantity remaining</th>
            <td>{{ batch.remaining_quantity }}</td>
          </tr>
          <tr>
            <th>Expiry date</th>
            <td>{{ utilService.bestBeforeTime(batch.expiry_date) }}</td>
          </tr>
          <tr>
            <th>Created</th>
            <td>{{ utilService.formatTime(batch.created_at) }}</td>
          </tr>
          <tr>
            <th>Updated</th>
            <td>{{ utilService.formatTime(batch.updated_at) }}</td>
          </tr>
        </table>
      </div>

      @if (batch.additional_information) {
        <div class="batch-view-card__section">
          <h2 class="batch-view-card__section-title">Additional information</h2>
          <quill-view [content]="batch.additional_information" format="html" theme="snow"></quill-view>
        </div>
      }

      <div class="batch-view-card__section">
        <h2 class="batch-view-card__section-title">Fulfilled orders</h2>
        @if (!fulfilledOrders) {
          <mat-progress-bar mode="buffer"></mat-progress-bar>
        }
        @if (fulfilledOrders) {
          @for (order of fulfilledOrders; track order) {
            <mat-card class="batch-view-card__order-card">
              <h3 class="batch-view-card__order-title">Order #{{ order.id }}</h3>
              <app-batch-order-item-list [order]="order" [orderItems]="order.order_items">
              </app-batch-order-item-list>
            </mat-card>
          }
        }
      </div>
    }

    @if (editMode && batch) {
      <form class="batch-view-card__form" [formGroup]="batchDetailsForm" (ngSubmit)="submitData()">
        <div class="row g-2">
          <div class="col-md-6">
            <mat-form-field appearance="outline" class="batch-view-card__field">
              <mat-label>Batch number</mat-label>
              <input required type="text" readonly formControlName="batchNumber" matInput placeholder="Batch number" />
              @if (batchDetailsForm.get('batchNumber')?.invalid) {
                <mat-error>Batch number is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" class="batch-view-card__field">
              <mat-label>Quantity</mat-label>
              <input required formControlName="quantity" matInput placeholder="Quantity" />
              @if (batchDetailsForm.get('quantity')?.invalid) {
                <mat-error>Batch quantity is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" class="batch-view-card__field">
              <mat-label>Select package type</mat-label>
              <mat-select required formControlName="packageTypeId">
                @for (type of medicineDosagePackageTypes; track type) {
                  <mat-option [value]="type.id">
                    {{ type.package_type.type }} ({{ type.contained_quantity }})
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="batch-view-card__field">
              <mat-label>Expiry date</mat-label>
              <input required data-testid="expiry-date" formControlName="expiryDate" matInput [matDatepicker]="picker1" />
              <mat-datepicker-toggle matSuffix [for]="picker1"></mat-datepicker-toggle>
              <mat-datepicker #picker1></mat-datepicker>
              @if (batchDetailsForm.get('expiryDate')?.invalid) {
                <mat-error>Expiry date is invalid</mat-error>
              }
            </mat-form-field>
          </div>

          <div class="col-md-6">
            <mat-form-field appearance="outline" class="batch-view-card__field">
              <mat-label>Medicine</mat-label>
              <input
                type="text"
                required
                placeholder="Search for medicine"
                aria-label="Search for medicine"
                matInput
                formControlName="medicine"
                [matAutocomplete]="auto"
              />
              <mat-autocomplete (optionSelected)="medicineSelected($event)" autoActiveFirstOption #auto="matAutocomplete">
                @for (med of medicine; track med) {
                  <mat-option [value]="med">
                    {{ med.name }}
                  </mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>

            <mat-form-field appearance="outline" class="batch-view-card__field">
              <mat-label>Dosage type</mat-label>
              <mat-select
                required
                formControlName="medicineDosageType"
                (selectionChange)="getMedicineDosagePackageTypes($event)"
              >
                @for (type of medicineDosageTypes; track type) {
                  <mat-option [value]="type">
                    {{ type.additional_information }}
                  </mat-option>
                }
              </mat-select>
              @if (batchDetailsForm.get('dosageTypeId')?.invalid) {
                <mat-error>Batch dosage type is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" class="batch-view-card__field">
              <mat-label>Status</mat-label>
              <mat-select required formControlName="statusId">
                @for (status of batchStatuses; track status) {
                  <mat-option
                    [disabled]="status.code === 'DPL' && batch.remaining_quantity > 0"
                    [value]="status.id"
                  >
                    {{ status.status }}
                  </mat-option>
                }
              </mat-select>
              @if (batchDetailsForm.get('statusId')?.invalid) {
                <mat-error>Status is required</mat-error>
              }
            </mat-form-field>
          </div>
        </div>

        <div class="batch-view-card__editor">
          <h2 class="batch-view-card__section-title">Additional information</h2>
          <quill-editor formControlName="additional_information" format="html" [styles]="{ minHeight: '100px' }">
          </quill-editor>
        </div>

        <div class="batch-view-card__footer">
          <button
            type="button"
            [disabled]="!batchDetailsForm.valid"
            mat-flat-button
            color="primary"
            class="batch-view-card__btn--primary"
            (click)="submitData()"
          >
            <mat-icon>save</mat-icon>
            Save
          </button>
        </div>
      </form>
    }
  </mat-card>
</div>
